import React, { useEffect, useState } from "react";
import {
  Card,
  Flex,
  Text,
  Button,
  Badge,
  Stack,
  Image,
  Switch,
  Popover,
  Tooltip,
  Box,
  Tabs,
  LoadingOverlay,
  Modal,
  Divider,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/ReduxStore";
import { User1 } from "../../@types/User";
import {
  CreateSubscriptionOptionData,
  VerifySubscriptionPayment,
} from "../../_parentsApp/features/paymentSlice";
import {
  convertToHyphenSeparated,
  loadScript,
} from "../../utilities/HelperFunctions";
import { showNotification } from "@mantine/notifications";
import { IconTemperature } from "@tabler/icons";
import {
  GetAllInfoForInstitute,
  updateFeaturesAndSubscription,
} from "../../_parentsApp/features/instituteSlice";
import { useNavigate } from "react-router-dom";
import { instituteDetails } from "../../store/instituteDetailsSlice";
import {
  LocalStorageKey,
  RemoveValueFromLocalStorage,
} from "../../utilities/LocalstorageUtility";
import { Mixpanel } from "../../utilities/Mixpanel/MixpanelHelper";
import { SubscriptionPlan, VIGNAMSUBSCRIPTIONS } from "./SubscriptionPlan";

const PricingCard = () => {
  const user1 = useSelector<RootState, User1 | null>((state) => {
    return state.currentUser.user;
  });
  const instituteDetails = useSelector<RootState, InstituteDetails | null>(
    (state) => state.instituteDetailsSlice.instituteDetails
  );
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isMd = useMediaQuery(`(max-width: 820px)`);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openPlanConvertModal, setOpenPlanConvertModal] =
    useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState("Monthly");
  const [plans, setPlans] = useState<SubscriptionPlan[]>(VIGNAMSUBSCRIPTIONS['monthly']);
  const [displayPlanButton, setDisplayPlanButton] = useState<number>(-1);
  const [upgradePlanPrice, setUpgradePlanPrice] = useState<number>(0);

  useEffect(() => {
    GetAllInfoForInstitute({ id: user1?.instituteId!! })
      .then((x: any) => {
        const today = new Date().toISOString().split("T")[0];
        const expirationDate = new Date(x.subscriptionExpirationDate)
          .toISOString()
          .split("T")[0];
        const planExpired = today >= expirationDate;
        if (x.subscriptionModelType === "PRO" && !planExpired) {
          setDisplayPlanButton(0);
        } else if (x.subscriptionModelType === "PRO PLUS" && !planExpired) {
          setDisplayPlanButton(1);
        } else if (!planExpired) {
          setDisplayPlanButton(1);
        }
        else{
          setDisplayPlanButton(-1);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  }, [user1]);

  useEffect(() => {
    setPlans(VIGNAMSUBSCRIPTIONS[selectedTab.toLowerCase()])
    let proPrice = 0;
    let proPlusPrice = 0;

    for (const plan of plans) {
      if (plan.name === "Pro") proPrice = plan?.discountedPrice!!;
      if (plan.name === "Pro plus") proPlusPrice = plan?.discountedPrice!!;
    }

    const priceDifference = proPlusPrice - proPrice;
    setUpgradePlanPrice(priceDifference);
  }, [selectedTab]);

  const MakePayment = (amount: number, plan: string, convertPlan: boolean) => {
    if (!convertPlan && displayPlanButton === 0) {
      setOpenPlanConvertModal(true);
      return;
    }
    CreateSubscriptionOptionData({
      amount,
      userId: user1?._id!!,
      instituteId: user1?.instituteId!!,
      type: selectedTab,
    })
      .then(async (x: any) => {
        const optionData = x.order;

        const res = await loadScript(
          "https://checkout.razorpay.com/v1/checkout.js"
        );

        if (!res) {
          alert("Razorpay SDK failed to load. Are you online?");
          return;
        }

        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          currency: optionData.currency,
          amount: optionData.amount,
          order_id: optionData.id,
          name: "Payment",
          description: "Thank you.",
          image:
            "https://vignam-content-images.s3.amazonaws.com/2023-08-17T11-55-12-035Z.png",
          handler: async function (response: any) {
            const successData = {
              orderCreationId: optionData.id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              id: optionData.paymentRecordId,
              amount: optionData.amount,
            };
            VerifySubscriptionPayment(successData)
              .then(() => {
                setIsLoading(true);
                updateFeatureAccess(user1?.instituteId!!, plan);
              })
              .catch((e) => {
                showNotification({ message: "Failed", color: "red" });
                console.log(e);
              });
          },
          prefill: {
            email: "",
            phone_number: "",
          },
        };
        //@ts-ignore
        const paymentObject = new window.Razorpay(options);
        paymentObject.open();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const updateFeatureAccess = (instituteId: string, plan: string) => {
    updateFeaturesAndSubscription({
      instituteId: instituteId,
      plan: plan,
      subscriptionType: selectedTab,
    })
      .then((x: any) => {
        setIsLoading(false);
        showNotification({
          message: "Payment success!",
          color: "green",
        });

        navigate(
          `/${convertToHyphenSeparated(user1?.instituteName!!)}/${
            user1?.instituteId
          }/teach1/dashboard`
        );
      })
      .catch((e) => {
        console.log(e);
      });
  };

  return (
    <Stack w={"100%"} h={"100vh"}>
      <LoadingOverlay visible={isLoading} zIndex={1002} pos={"fixed"} />
      <Flex
        w={"90%"}
        align={"center"}
        justify={"space-between"}
        mx={"auto"}
        mt={"1rem"}
        gap={5}
      >
        <Flex
          w={"84%"}
          align={isMd ? "center" : "center"}
          justify={isMd ? "center" : "start"}
          mx={"auto"}
          gap={5}
        >
          <Image
            src={require("../../assets/newLogo.png")}
            style={{ width: "2rem" }}
          />
          <Text fz={24} weight={700}>
            Vignam
          </Text>
        </Flex>
        <Button
          variant="outline"
          color="#4B65F6"
          sx={{
            "&:hover": {
              color: "#fff",
              backgroundColor: "#4B65F6",
            },
          }}
          onClick={() => {
            RemoveValueFromLocalStorage(LocalStorageKey.Token);
            RemoveValueFromLocalStorage(LocalStorageKey.User);
            Mixpanel.logout();
            navigate("/");
          }}
        >
          Logout
        </Button>
      </Flex>
      <Divider size={1} c={"gray"} />
      <Text
        fz={isMd ? "1.5rem" : "3rem"}
        weight={700}
        ta={"center"}
        lh={1.2}
        mt={"2rem"}
      >
        Grow with VIGNAM.
      </Text>
      <Text fz={isMd ? "1rem" : "1.5rem"} lh={1} weight={700} ta={"center"}>
        Build Stunning 3D Content Without Any Techincal Expertise
      </Text>
      <Flex w={"100%"} align={"center"} justify={"center"} gap={10} m={24}>
        <Tabs
          color="blue"
          defaultValue="Monthly"
          onTabChange={(value: string) => setSelectedTab(value)}
        >
          <Tabs.List defaultValue={"Monthly"}>
            <Tabs.Tab
              value="Monthly"
              fw={selectedTab === "Monthly" ? 800 : 500}
              color="blue"
            >
              Monthly
            </Tabs.Tab>
            <Tabs.Tab
              value="Quarterly"
              color="blue"
              fw={selectedTab === "Quarterly" ? 800 : 500}
            >
              Quarterly
            </Tabs.Tab>
            <Tabs.Tab
              value="Yearly"
              color="blue"
              fw={selectedTab === "Yearly" ? 800 : 500}
            >
              Yearly
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Flex>
      <Flex justify="center" align="center" wrap="wrap" gap="xl" m={16}>
        {plans.map((plan, index) => (
          <Card
            key={index}
            shadow="md"
            p="lg"
            sx={{
              width: isMd ? 360 : 400,
              minHeight: 420,
              border: "1px solid #e9ecef",
              borderRadius: "2rem",
              cursor: "pointer",
              transition: "border-color 0.3s ease",

              "&:hover": {
                borderColor: "#4B65F6",
              },
              "&:hover button": {
                borderColor: "#4B65F6",
              },
            }}
          >
            <Stack align="start">
              <Flex align={"center"} gap={15}>
                <Text fz="1.4rem" weight={700}>
                  {plan.name}
                </Text>
                {plan.badge && (
                  <Badge color="blue" variant="light" size="md">
                    {plan.badge}
                  </Badge>
                )}
              </Flex>
              <Text align="start" color="black" size="md">
                {plan.description}
              </Text>
              <Flex gap={10} align={"center"} justify={"start"}>
                {plan.originalPrice && <Text strikethrough weight={800} color="dimmed" fz="2.5rem">
                  {plan.originalPrice}
                </Text>}
                {plan.discountedPrice ? (
                  <Text fz={32} weight={800}>
                    ₹{plan.discountedPrice}
                  </Text>
                ) : (
                  <Text fz={26} weight={800}>
                    Plans start from ₹2,00,000
                  </Text>
                )}
                { plan.discountedPrice ? 
                <Text fz={16}>+GST*</Text>
              :<></>
                }
              </Flex>
              {
              plan.discountedPrice ? (
                <Text fz="1rem" weight={500} lh={1}>
                  Billed {selectedTab}
                </Text>
              ): <Text fz="1rem" weight={500} lh={1}>
              Billed Yearly
            </Text>
            }
              <Stack align="flex-start" spacing="xs">
                {plan.items.map((item, idx) => (
                  <Flex key={idx} align={"center"} gap={5}>
                    <Text size="md" ff={"Poppins"}>
                      • {item.label}
                    </Text>

                    <Tooltip
                      label={
                        <Box>
                          <Text size="sm">{item.detailed}</Text>
                        </Box>
                      }
                      position="top"
                      withArrow
                      transition="fade"
                      transitionDuration={200}
                      styles={{
                        tooltip: {
                          maxWidth: "200px",
                          maxHeight: "200px",
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          padding: "10px",
                          fontSize: "14px",
                          backgroundColor: "#1c7ed6",
                          color: "#fff",
                          borderRadius: "8px",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                        },
                        arrow: {
                          backgroundColor: "#1c7ed6",
                        },
                      }}
                    >
                      <Image
                        src={require("../../assets/infoBtn2.png")}
                        style={{ width: "0.9rem", cursor: "pointer" }}
                      />
                    </Tooltip>
                  </Flex>
                ))}
              </Stack>
              <Button
                mt="md"
                w={"90%"}
                size="md"
                variant={"outline"}
                color={index === 1 ? "blue" : "dark"}
                disabled={index <= displayPlanButton}
                sx={{
                  borderRadius: "2rem",
                  "&:hover": {
                    color: "#fff",
                    backgroundColor: "#4B65F6",
                  },
                  position: "absolute",
                  bottom: "1rem",
                }}
                onClick={() => {
                  if (index < 2) {
                    MakePayment(
                      Number(plan.discountedPrice)*1.18,
                      plan.name!!,
                      false
                    );
                  } else {
                    window.location.href =
                      "https://calendly.com/vibhanshu-vignam/product-demo";
                  }
                }}
              >
                {plan.discountedPrice
                  ? index <= displayPlanButton
                    ? `plan expire on      ${new Date(
                        instituteDetails?.subscriptionExpirationDate!
                      )?.toLocaleDateString("en-US")}`
                    : " Buy now"
                  : "Contact Sales"}
              </Button>
            </Stack>
          </Card>
        ))}
      </Flex>

      <Modal
        opened={openPlanConvertModal}
        onClose={() => setOpenPlanConvertModal(false)}
        title="Upgrade your plan"
        size={"md"}
      >
        <Stack align="center" justify="center">
          <Flex
            pt={"1rem"}
            pb={"4rem"}
            align={"center"}
            justify={"center"}
            gap={10}
          >
            <Text fz="1rem" weight={500} lh={1}>
              {" "}
              Convert your plan in{" "}
            </Text>
            <Text fz="2rem" weight={800} lh={1}>
              ${upgradePlanPrice}
            </Text>
          </Flex>
          <Button
            mt="15rem"
            size="md"
            variant={"outline"}
            color={"blue"}
            sx={{
              borderRadius: "2rem",
              "&:hover": {
                color: "#fff",
                backgroundColor: "#4B65F6",
              },
              position: "absolute",
              bottom: "1rem",
            }}
            onClick={() => MakePayment(upgradePlanPrice, "Pro plus", true)}
          >
            Upgrade now
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default PricingCard;
